// /modules/vehiculos/components/BusquedaMasivaTab.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { compararListaPlacas } from "../services/vehiculosService";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

const cellSx = { py: 0.4, px: 0.75, verticalAlign: "top" };
const badgeSx = { height: 18, fontSize: "0.63rem", mr: 0.4, mb: 0.4 };

const detectDelimiter = (text) => {
  const lines = text.split(/\r\n|\r|\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const counts = {
      "\t": (line.match(/\t/g) || []).length,
      ",": (line.match(/,/g) || []).length,
      ";": (line.match(/;/g) || []).length,
      "|": (line.match(/\|/g) || []).length,
    };
    const [topDelim, topCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return topCount > 0 ? topDelim : "\t";
  }
  return "\t";
};

const parsePasted = (text, plateCol) => {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const delimiter = detectDelimiter(trimmed);
  const lines = trimmed.split(/\r\n|\r|\n/);
  const placas = [];
  lines.forEach((line) => {
    const clean = line.replace(/ /g, " ").trim();
    if (!clean) return;
    const cells = clean.split(delimiter);
    const val = cells[plateCol];
    if (val && val.trim()) placas.push(val.trim());
  });
  return placas;
};

function FuenteVacia() {
  return (
    <Typography sx={{ fontSize: "0.68rem", color: "text.disabled", fontStyle: "italic" }}>
      Sin datos
    </Typography>
  );
}

export default function BusquedaMasivaTab() {
  const [pasted, setPasted] = useState("");
  const [plateCol, setPlateCol] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const handleComparar = async () => {
    const placas = parsePasted(pasted, Math.max(0, (Number(plateCol) || 1) - 1));
    if (!placas.length) {
      setError("Debe pegar al menos una placa.");
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const data = await compararListaPlacas(placas);
      setResultado(data);
    } catch (err) {
      console.error(err);
      setError("Error al comparar la lista.");
    } finally {
      setCargando(false);
    }
  };

  const filas = resultado?.data ?? [];

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, mb: 0.5 }}>
          Pegar placas (Ctrl+V) — una por línea, o columnas separadas por tab/coma/;
        </Typography>
        <TextField
          multiline
          minRows={5}
          maxRows={12}
          fullWidth
          spellCheck={false}
          placeholder={"4773-HHE\n1132-ATS\n..."}
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          sx={{ mb: 1, "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.75rem" } }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Columna de placa"
            type="number"
            size="small"
            value={plateCol}
            onChange={(e) => setPlateCol(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ width: 140, "& .MuiInputBase-root": { height: 32 }, "& .MuiInputBase-input": { fontSize: "0.78rem", py: 0.5 } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleComparar}
            disabled={cargando || !pasted.trim()}
            sx={{ height: 32, fontSize: "0.75rem", px: 1.5 }}
          >
            {cargando ? "Comparando..." : "Comparar"}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
          {error}
        </Alert>
      )}
      {cargando && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {resultado && (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Typography
            sx={{
              px: 1,
              py: 0.5,
              fontSize: "0.72rem",
              fontWeight: 800,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100]),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            Resultado ({filas.length} placa{filas.length === 1 ? "" : "s"})
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Placa</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Módems (SORT)</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Wialon</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Leasecontrol</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Rastreo BO</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>YPFB Nacional</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>YPFB Internacional</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Lista Ximena Nacional</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Lista Ximena Internacional</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.map((f) => (
                  <TableRow key={f.placaKey || f.placa} hover>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{f.placa}</Typography>
                    </TableCell>

                    {/* Módems SORT */}
                    <TableCell sx={cellSx}>
                      {f.modems?.length ? (
                        f.modems.map((m, i) => {
                          const label = m.num_sim || m.ID_A || m.emai || "modem";
                          const priv = [m.nombre_normal, m.descripcion_normal].filter(Boolean).join(" - ") || "-";
                          const ypfb = m.descripcion_ypfb || "-";
                          return (
                            <Box key={`${label}-${i}`} sx={{ mb: 0.4 }}>
                              <Chip
                                size="small"
                                label={label}
                                onClick={() => setEquipoDialog({ tipo: "modem", identificador: label })}
                                sx={{ ...badgeSx, cursor: "pointer" }}
                                color="default"
                              />
                              <Chip size="small" label={priv} sx={{ ...badgeSx, bgcolor: "info.light" }} />
                              <Chip size="small" label={ypfb} sx={{ ...badgeSx, bgcolor: "primary.light" }} />
                              <Chip size="small" label={m.estado || "-"} color="success" sx={badgeSx} />
                            </Box>
                          );
                        })
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* Wialon */}
                    <TableCell sx={cellSx}>
                      {f.wialon?.length ? (
                        f.wialon.map((w, i) => (
                          <Box key={i} sx={{ mb: 0.4 }}>
                            <Chip size="small" label={w.nm || "-"} sx={{ ...badgeSx, bgcolor: "info.light" }} />
                            {w.uid && <Chip size="small" label={`UID: ${w.uid}`} sx={badgeSx} />}
                          </Box>
                        ))
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* Leasecontrol */}
                    <TableCell sx={cellSx}>
                      {f.leasecontrol?.length ? (
                        f.leasecontrol.map((w, i) => (
                          <Box key={i} sx={{ mb: 0.4 }}>
                            <Chip size="small" label={w.nm || "-"} sx={{ ...badgeSx, bgcolor: "info.light" }} />
                            {w.uid && <Chip size="small" label={`UID: ${w.uid}`} sx={badgeSx} />}
                          </Box>
                        ))
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* Rastreo BO */}
                    <TableCell sx={cellSx}>
                      {f.rastreoBo?.length ? (
                        f.rastreoBo.map((b, i) => (
                          <Box key={i} sx={{ mb: 0.4 }}>
                            {b.modem && <Chip size="small" label={`MODEM: ${b.modem}`} color="warning" sx={badgeSx} />}
                            <Chip size="small" label={`Fecha: ${b.fecha || "-"}`} sx={badgeSx} />
                          </Box>
                        ))
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* YPFB Nacional */}
                    <TableCell sx={cellSx}>
                      {f.ypfbNacional?.length ? (
                        f.ypfbNacional.map((n, i) => (
                          <Box key={i} sx={{ mb: 0.4 }}>
                            {n.modId && <Chip size="small" label={`modId: ${n.modId}`} color="primary" sx={badgeSx} />}
                            {n.modem && <Chip size="small" label={`MODEM: ${n.modem}`} color="warning" sx={badgeSx} />}
                            {n.alias && <Chip size="small" label={n.alias} sx={{ ...badgeSx, bgcolor: "info.light" }} />}
                          </Box>
                        ))
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* YPFB Internacional */}
                    <TableCell sx={cellSx}>
                      {f.ypfbInternacional?.length ? (
                        f.ypfbInternacional.map((n, i) => (
                          <Box key={i} sx={{ mb: 0.4 }}>
                            {n.modId && <Chip size="small" label={`modId: ${n.modId}`} color="primary" sx={badgeSx} />}
                            {n.modem && <Chip size="small" label={`MODEM: ${n.modem}`} color="warning" sx={badgeSx} />}
                            {n.alias && <Chip size="small" label={n.alias} sx={{ ...badgeSx, bgcolor: "info.light" }} />}
                          </Box>
                        ))
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* Lista Ximena Nacional */}
                    <TableCell sx={cellSx}>
                      {f.ximenaNacional ? (
                        <Box>
                          {f.ximenaNacional.empresa && <Chip size="small" label={f.ximenaNacional.empresa} color="primary" sx={badgeSx} />}
                          {f.ximenaNacional.alias && <Chip size="small" label={f.ximenaNacional.alias} sx={{ ...badgeSx, bgcolor: "info.light" }} />}
                          {f.ximenaNacional.observaciones && <Chip size="small" label={f.ximenaNacional.observaciones} color="warning" sx={badgeSx} />}
                        </Box>
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>

                    {/* Lista Ximena Internacional */}
                    <TableCell sx={cellSx}>
                      {f.ximenaInternacional ? (
                        <Box>
                          {f.ximenaInternacional.empresa && <Chip size="small" label={f.ximenaInternacional.empresa} color="primary" sx={badgeSx} />}
                          {f.ximenaInternacional.asociacion && <Chip size="small" label={f.ximenaInternacional.asociacion} sx={{ ...badgeSx, bgcolor: "info.light" }} />}
                          {f.ximenaInternacional.lote && <Chip size="small" label={`Lote: ${f.ximenaInternacional.lote}`} color="warning" sx={badgeSx} />}
                          {f.ximenaInternacional.base && <Chip size="small" label={f.ximenaInternacional.base} color="success" sx={badgeSx} />}
                        </Box>
                      ) : (
                        <FuenteVacia />
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={cellSx}>
                      <Typography sx={{ fontSize: "0.75rem" }} color="text.secondary">
                        Sin resultados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        title="Detalle del módem"
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
}
